import type { GameState, StatusInstance, PlayerState } from "@/types/game";
import { nextId } from "@/utils/id";

// 状态名称 → 描述
const STATUS_DESCRIPTIONS: Record<string, string> = {
  沉默: "不能使用法术牌",
  禁攻: "不能打出攻击牌",
  无魔御: "魔法防御降为0",
  致命弱点: "受到的伤害翻倍",
  无间炼狱: "不能打出效果牌",
  剧毒: "每回合开始损失生命",
  寒冰: "冻结，跳过回合",
  命运链接: "与链接对象共享伤害和治疗",
  禁疗: "无法恢复生命",
  赤月鸟减攻: "攻击力-10",
  霸者减防: "防御-3，魔御-5",
};

export function statusDescription(name: string): string {
  return STATUS_DESCRIPTIONS[name] ?? "";
}

// 创建状态实例
export function createStatus(
  name: string,
  source: string,
  remainingTurns: number,
  modifiers: Partial<StatusInstance["modifiers"]> = {}
): StatusInstance {
  return {
    id: nextId("st"),
    name,
    remainingTurns,
    source,
    modifiers,
  };
}

// 给玩家附加状态
// 跨玩家状态（source !== 携带者）打 justAppliedCrossPlayer=true：
// 挂上后的第一次释放者回合结束时跳过 tick，等到下一次才递减。
// 即 A 给 B 挂持续 1 的状态，B 在 A 下家时，要等到 A 的下一次回合结束才消失。
export function addStatus(
  player: PlayerState,
  status: StatusInstance
): PlayerState {
  const newStatus =
    status.source !== player.id && status.remainingTurns !== -1
      ? { ...status, justAppliedCrossPlayer: true }
      : status;
  return { ...player, statuses: [...player.statuses, newStatus] };
}

// 移除指定名字的状态
export function removeStatus(
  player: PlayerState,
  name: string
): PlayerState {
  return {
    ...player,
    statuses: player.statuses.filter((s) => s.name !== name),
  };
}

// 回合结束时的状态结算：tick 由 sourceId 释放的状态（remainingTurns--，到期移除）
// 规则：状态在"释放者"的回合结束时 tick，而不是"携带者"的回合结束。
// 即 A 给 B 挂持续 1 的状态，要等 A 的下一个回合结束才消失（跨过 B/C/D 的回合）。
// 跨玩家状态挂上后的第一次释放者回合结束跳过 tick（justAppliedCrossPlayer flag）。
// 永久状态（remainingTurns === -1）保留；其余 ≤0 时移除
export function tickStatusesBySource(
  player: PlayerState,
  sourceId: string
): PlayerState {
  const next = player.statuses
    .map((s) => {
      // 不是当前回合玩家释放的，不动
      if (s.source !== sourceId) return s;
      // 永久状态不动
      if (s.remainingTurns === -1) return s;
      // 跨玩家状态：挂上后第一次释放者回合结束跳过 tick，仅清除 flag
      if (s.justAppliedCrossPlayer) {
        return { ...s, justAppliedCrossPlayer: false };
      }
      // 正常 tick
      return { ...s, remainingTurns: s.remainingTurns - 1 };
    })
    .filter((s) => s.remainingTurns === -1 || s.remainingTurns > 0);
  return { ...player, statuses: next };
}

// 兼容旧调用：tick 玩家身上的所有状态（仅用于无 source 上下文的场景）
export function tickStatuses(player: PlayerState): PlayerState {
  const next = player.statuses
    .map((s) =>
      s.remainingTurns === -1
        ? s
        : { ...s, remainingTurns: s.remainingTurns - 1 }
    )
    .filter((s) => s.remainingTurns === -1 || s.remainingTurns > 0);
  return { ...player, statuses: next };
}

// 是否被沉默
export function isSilenced(player: PlayerState): boolean {
  return player.statuses.some((s) => s.modifiers?.silenced);
}

// 是否无敌
export function isInvulnerable(player: PlayerState): boolean {
  return player.statuses.some((s) => s.modifiers?.invulnerable);
}

// 是否禁疗
export function isNoHeal(player: PlayerState): boolean {
  return player.statuses.some((s) => s.modifiers?.noHeal);
}

// 是否禁攻（神龙摆尾）
export function isAttackLocked(player: PlayerState): boolean {
  return player.statuses.some((s) => s.modifiers?.attackLocked);
}

// 是否禁效果牌（无间炼狱）
export function isEffectLocked(player: PlayerState): boolean {
  return player.statuses.some((s) => s.modifiers?.effectLocked);
}

// 是否被冻结（寒冰：跳过回合 + 回合外不能打出任何手牌）
export function isFrozen(player: PlayerState): boolean {
  return player.statuses.some((s) => s.modifiers?.frozen);
}

// 是否跳过回合（寒冰冻结）
export function isStunned(player: PlayerState): boolean {
  return isFrozen(player);
}

// 是否无魔御（巨剑之舞）
export function hasNoMagicDefense(player: PlayerState): boolean {
  return player.statuses.some((s) => s.modifiers?.noMagicDefense);
}

// 是否嗜血（本执行阶段可出任意张攻击/法术）
export function hasBloodlust(player: PlayerState): boolean {
  return player.statuses.some((s) => s.modifiers?.bloodlust);
}

// 致命弱点：受到的任意伤害翻倍
export function damageTakenMultiplier(player: PlayerState): number {
  return player.statuses.some((s) => s.name === "致命弱点") ? 2 : 1;
}

// 剧毒：每个玩家回合前损失生命 N（取最大值，不叠加）
export function poisonAmount(player: PlayerState): number {
  let v = 0;
  for (const s of player.statuses) {
    if (s.modifiers?.poison && s.modifiers.poison > v) v = s.modifiers.poison;
  }
  return v;
}

// 命运链接：链接对象玩家 id（未链接返回 undefined）
export function fateLinkPartner(player: PlayerState): string | undefined {
  const st = player.statuses.find((s) => s.name === "命运链接");
  return st?.linkedTo;
}

// 给玩家附加状态并记录事件
export function addStatusWithEvent(
  state: GameState,
  playerId: string,
  status: StatusInstance
): GameState {
  return {
    ...state,
    players: state.players.map((p) => (p.id === playerId ? addStatus(p, status) : p)),
    log: [
      ...state.log,
      {
        type: "status-add",
        playerId,
        statusName: status.name,
        duration: status.remainingTurns,
      },
    ],
  };
}

// 移除玩家指定名字的状态并记录事件
export function removeStatusWithEvent(
  state: GameState,
  playerId: string,
  name: string
): GameState {
  const player = state.players.find((p) => p.id === playerId);
  if (!player || !player.statuses.some((s) => s.name === name)) return state;
  return {
    ...state,
    players: state.players.map((p) =>
      p.id === playerId ? removeStatus(p, name) : p
    ),
    log: [...state.log, { type: "status-remove", playerId, statusName: name }],
  };
}
